import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  message,
  Typography,
  Space,
  Tag,
  Row,
  Col,
  Statistic,
  Popconfirm,
  Tabs,
  DatePicker
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ShopOutlined,
  DollarOutlined,
  PhoneOutlined,
  MailOutlined,
  FileTextOutlined,
  FileExcelOutlined
} from '@ant-design/icons';
import { stationOwnerAPI } from '../services/api';
import { handleExport } from '../utils/exportUtils';
import dayjs from 'dayjs';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

interface Supplier {
  _id: string;
  companyName: string;
  contactPerson?: string;
  email?: string;
  phone: string;
  category: string;
  totalOrders: number;
  totalSpent: number;
  rating: number;
  isActive: boolean;
  address?: {
    street?: string;
    city?: string;
    district?: string;
    postalCode?: string;
    country?: string;
  };
}

interface SupplierOrder {
  _id: string;
  orderNumber: string;
  supplier: {
    _id: string;
    companyName: string;
  };
  station: {
    _id: string;
    name: string;
  };
  status: string;
  paymentStatus: string;
  total: number;
  orderDate: string;
  expectedDeliveryDate?: string;
}

const Suppliers: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [orders, setOrders] = useState<SupplierOrder[]>([]);
  const [stations, setStations] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [supplierModalVisible, setSupplierModalVisible] = useState(false);
  const [orderModalVisible, setOrderModalVisible] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<SupplierOrder | null>(null);
  const [activeTab, setActiveTab] = useState('suppliers');
  const [filters, setFilters] = useState<any>({
    category: 'all',
    isActive: 'all',
    search: ''
  });
  const [form] = Form.useForm();
  const [orderForm] = Form.useForm();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    fetchStations();
    fetchSuppliers();
    if (activeTab === 'orders') {
      fetchOrders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, filters]);

  const fetchStations = async () => {
    try {
      const response = await stationOwnerAPI.getStations();
      setStations(response);
    } catch (error) {
      message.error('İstasyonlar yüklenemedi');
    }
  };

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await stationOwnerAPI.getSuppliers(filters);
      setSuppliers(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Tedarikçiler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrders = async () => {
    setOrdersLoading(true);
    try {
      const response = await stationOwnerAPI.getSupplierOrders();
      setOrders(response);
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Siparişler yüklenemedi');
    } finally {
      setOrdersLoading(false);
    }
  };

  const handleAddSupplier = () => {
    setSelectedSupplier(null);
    form.resetFields();
    form.setFieldsValue({
      category: 'fuel',
      paymentTerms: 'net_30',
      isActive: true
    });
    setSupplierModalVisible(true);
  };

  const handleEditSupplier = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    form.setFieldsValue({
      ...supplier,
      address: supplier.address || {
        street: '',
        city: '',
        district: '',
        postalCode: '',
        country: 'Türkiye'
      }
    });
    setSupplierModalVisible(true);
  };

  const handleSubmitSupplier = async (values: any) => {
    try {
      if (selectedSupplier) {
        await stationOwnerAPI.updateSupplier(selectedSupplier._id, values);
        message.success('Tedarikçi güncellendi');
      } else {
        await stationOwnerAPI.createSupplier(values);
        message.success('Tedarikçi oluşturuldu');
      }
      setSupplierModalVisible(false);
      form.resetFields();
      setSelectedSupplier(null);
      fetchSuppliers();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDeleteSupplier = async (id: string) => {
    try {
      await stationOwnerAPI.deleteSupplier(id);
      message.success('Tedarikçi silindi');
      fetchSuppliers();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleAddOrder = () => {
    setSelectedOrder(null);
    orderForm.resetFields();
    orderForm.setFieldsValue({
      orderDate: dayjs(),
      status: 'pending',
      paymentStatus: 'pending',
      paymentMethod: 'bank_transfer',
      items: [{ productName: '', quantity: 1, unit: 'adet', unitPrice: 0, taxRate: 0 }],
      discount: 0,
      shippingCost: 0
    });
    setOrderModalVisible(true);
  };

  const handleEditOrder = (order: SupplierOrder) => {
    setSelectedOrder(order);
    orderForm.setFieldsValue({
      ...order,
      stationId: order.station._id,
      supplierId: order.supplier._id,
      orderDate: order.orderDate ? dayjs(order.orderDate) : dayjs(),
      expectedDeliveryDate: order.expectedDeliveryDate ? dayjs(order.expectedDeliveryDate) : undefined
    });
    setOrderModalVisible(true);
  };

  const handleSubmitOrder = async (values: any) => {
    try {
      const data = {
        stationId: values.stationId,
        supplierId: values.supplierId,
        items: values.items.map((item: any) => ({
          productName: item.productName,
          description: item.description,
          quantity: item.quantity,
          unit: item.unit,
          unitPrice: item.unitPrice,
          taxRate: item.taxRate || 0
        })),
        discount: values.discount || 0,
        shippingCost: values.shippingCost || 0,
        orderDate: values.orderDate?.toISOString(),
        expectedDeliveryDate: values.expectedDeliveryDate?.toISOString(),
        status: values.status,
        paymentStatus: values.paymentStatus,
        paymentMethod: values.paymentMethod,
        trackingNumber: values.trackingNumber,
        notes: values.notes
      };

      if (selectedOrder) {
        await stationOwnerAPI.updateSupplierOrder(selectedOrder._id, data);
        message.success('Sipariş güncellendi');
      } else {
        await stationOwnerAPI.createSupplierOrder(data);
        message.success('Sipariş oluşturuldu');
      }
      setOrderModalVisible(false);
      orderForm.resetFields();
      setSelectedOrder(null);
      fetchOrders();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const handleDeleteOrder = async (id: string) => {
    try {
      await stationOwnerAPI.deleteSupplierOrder(id);
      message.success('Sipariş silindi');
      fetchOrders();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'İşlem başarısız');
    }
  };

  const supplierColumns = [
    {
      title: 'Şirket Adı',
      dataIndex: 'companyName',
      key: 'companyName',
      render: (text: string, record: Supplier) => (
        <Space direction="vertical" size={0}>
          <span style={{ fontWeight: 'bold' }}>{text}</span>
          {record.contactPerson && (
            <span style={{ fontSize: '12px', color: '#666' }}>{record.contactPerson}</span>
          )}
        </Space>
      )
    },
    {
      title: 'İletişim',
      key: 'contact',
      render: (record: Supplier) => (
        <Space direction="vertical" size={0}>
          {record.phone && (
            <span><PhoneOutlined /> {record.phone}</span>
          )}
          {record.email && (
            <span><MailOutlined /> {record.email}</span>
          )}
        </Space>
      )
    },
    {
      title: 'Kategori',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => {
        const colors: any = {
          fuel: 'red',
          lubricant: 'blue',
          equipment: 'green',
          service: 'orange',
          food: 'purple',
          other: 'default'
        };
        const labels: any = {
          fuel: 'Yakıt',
          lubricant: 'Yağ',
          equipment: 'Ekipman',
          service: 'Hizmet',
          food: 'Yiyecek',
          other: 'Diğer'
        };
        return <Tag color={colors[category]}>{labels[category] || category}</Tag>;
      }
    },
    {
      title: 'Toplam Sipariş',
      dataIndex: 'totalOrders',
      key: 'totalOrders',
      render: (count: number) => count || 0
    },
    {
      title: 'Toplam Harcama',
      dataIndex: 'totalSpent',
      key: 'totalSpent',
      render: (amount: number) => `${(amount || 0).toLocaleString('tr-TR')} ₺`
    },
    {
      title: 'Durum',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Aktif' : 'Pasif'}
        </Tag>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: Supplier) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditSupplier(record)}
          />
          <Popconfirm
            title="Bu tedarikçiyi silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteSupplier(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const orderColumns = [
    {
      title: 'Sipariş No',
      dataIndex: 'orderNumber',
      key: 'orderNumber',
      render: (text: string) => <span style={{ fontWeight: 'bold' }}>{text}</span>
    },
    {
      title: 'Tedarikçi',
      dataIndex: ['supplier', 'companyName'],
      key: 'supplier'
    },
    {
      title: 'İstasyon',
      dataIndex: ['station', 'name'],
      key: 'station'
    },
    {
      title: 'Durum',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: any = {
          pending: 'default',
          confirmed: 'processing',
          processing: 'warning',
          shipped: 'blue',
          delivered: 'success',
          cancelled: 'error',
          returned: 'error'
        };
        const labels: any = {
          pending: 'Beklemede',
          confirmed: 'Onaylandı',
          processing: 'İşleniyor',
          shipped: 'Kargoda',
          delivered: 'Teslim Edildi',
          cancelled: 'İptal',
          returned: 'İade'
        };
        return <Tag color={colors[status]}>{labels[status] || status}</Tag>;
      }
    },
    {
      title: 'Ödeme',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (status: string) => {
        const colors: any = {
          pending: 'warning',
          partial: 'processing',
          paid: 'success',
          overdue: 'error'
        };
        const labels: any = {
          pending: 'Beklemede',
          partial: 'Kısmi',
          paid: 'Ödendi',
          overdue: 'Gecikmiş'
        };
        return <Tag color={colors[status]}>{labels[status] || status}</Tag>;
      }
    },
    {
      title: 'Tutar',
      dataIndex: 'total',
      key: 'total',
      render: (total: number) => (
        <span style={{ fontWeight: 'bold' }}>
          {total.toLocaleString('tr-TR')} ₺
        </span>
      )
    },
    {
      title: 'Tarih',
      dataIndex: 'orderDate',
      key: 'orderDate',
      render: (date: string) => dayjs(date).format('DD MMM YYYY')
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_: any, record: SupplierOrder) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditOrder(record)}
          />
          <Popconfirm
            title="Bu siparişi silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDeleteOrder(record._id)}
          >
            <Button type="link" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const totalSuppliers = suppliers.length;
  const activeSuppliers = suppliers.filter(s => s.isActive).length;
  const totalSpent = suppliers.reduce((sum, s) => sum + (s.totalSpent || 0), 0);
  const totalOrdersCount = suppliers.reduce((sum, s) => sum + (s.totalOrders || 0), 0);

  const tabItems = [
    {
      key: 'suppliers',
      label: 'Tedarikçiler',
      children: (
        <>
          {/* Özet İstatistikler */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Toplam Tedarikçi"
                  value={totalSuppliers}
                  prefix={<ShopOutlined />}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Aktif Tedarikçi"
                  value={activeSuppliers}
                  prefix={<ShopOutlined />}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Toplam Harcama"
                  value={totalSpent}
                  prefix={<DollarOutlined />}
                  precision={0}
                  suffix="₺"
                />
              </Card>
            </Col>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Toplam Sipariş"
                  value={totalOrdersCount}
                  prefix={<FileTextOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Filtreler */}
          <Card style={{ marginBottom: 24 }} styles={{ body: { padding: isMobile ? '12px' : '16px' } }}>
            <Row gutter={[8, 8]}>
              <Col xs={24} sm={12} md={8}>
                <Input
                  placeholder="Ara (şirket adı, kişi, telefon, email)"
                  value={filters.search}
                  onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                  allowClear
                  size={isMobile ? 'small' : 'middle'}
                />
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  value={filters.category}
                  onChange={(value) => setFilters({ ...filters, category: value })}
                  style={{ width: '100%' }}
                  placeholder="Kategori"
                  size={isMobile ? 'small' : 'middle'}
                >
                  <Option value="all">Tümü</Option>
                  <Option value="fuel">Yakıt</Option>
                  <Option value="lubricant">Yağ</Option>
                  <Option value="equipment">Ekipman</Option>
                  <Option value="service">Hizmet</Option>
                  <Option value="food">Yiyecek</Option>
                  <Option value="other">Diğer</Option>
                </Select>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <Select
                  value={filters.isActive}
                  onChange={(value) => setFilters({ ...filters, isActive: value })}
                  style={{ width: '100%' }}
                  placeholder="Durum"
                  size={isMobile ? 'small' : 'middle'}
                >
                  <Option value="all">Tümü</Option>
                  <Option value="true">Aktif</Option>
                  <Option value="false">Pasif</Option>
                </Select>
              </Col>
            </Row>
          </Card>

          <Card styles={{ body: { padding: isMobile ? '8px' : '24px' } }}>
            <Table
              columns={supplierColumns}
              dataSource={suppliers}
              rowKey="_id"
              loading={loading}
              pagination={{ 
                pageSize: 10,
                showSizeChanger: false,
                responsive: true
              }}
              scroll={{ x: 'max-content', y: isMobile ? '60vh' : undefined }}
              size={isMobile ? 'small' : 'middle'}
            />
          </Card>
        </>
      )
    },
    {
      key: 'orders',
      label: 'Siparişler',
      children: (
        <Card styles={{ body: { padding: isMobile ? '8px' : '24px' } }}>
          <Table
            columns={orderColumns}
            dataSource={orders}
            rowKey="_id"
            loading={ordersLoading}
            pagination={{ 
              pageSize: 10,
              showSizeChanger: false,
              responsive: true
            }}
            scroll={{ x: 'max-content', y: isMobile ? '60vh' : undefined }}
            size={isMobile ? 'small' : 'middle'}
          />
        </Card>
      )
    }
  ];

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} md={12}>
          <Title level={2} style={{ margin: 0, fontSize: isMobile ? '20px' : '24px' }}>
            <ShopOutlined /> Tedarikçi Yönetimi
          </Title>
        </Col>
        <Col xs={24} md={12}>
          <Space direction={isMobile ? 'vertical' : 'horizontal'} style={{ width: isMobile ? '100%' : 'auto' }} size="small">
            {activeTab === 'suppliers' && (
              <>
                <Button 
                  type="primary" 
                  icon={<PlusOutlined />} 
                  onClick={handleAddSupplier} 
                  block={isMobile}
                >
                  Yeni Tedarikçi Ekle
                </Button>
                <Button 
                  icon={<FileExcelOutlined />}
                  onClick={() => handleExport(
                    () => stationOwnerAPI.exportSuppliersExcel(),
                    `tedarikciler_${Date.now()}.xlsx`,
                    () => message.success('Excel başarıyla indirildi')
                  )}
                  block={isMobile}
                >
                  Excel İndir
                </Button>
              </>
            )}
            {activeTab === 'orders' && (
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={handleAddOrder} 
                block={isMobile}
              >
                Yeni Sipariş Oluştur
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Card>
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
        />
      </Card>

      {/* Tedarikçi Modal */}
      <Modal
        title={selectedSupplier ? 'Tedarikçi Düzenle' : 'Yeni Tedarikçi Ekle'}
        open={supplierModalVisible}
        onCancel={() => {
          setSupplierModalVisible(false);
          setSelectedSupplier(null);
          form.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 800}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmitSupplier}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="companyName"
                label="Şirket Adı"
                rules={[{ required: true, message: 'Şirket adı gereklidir' }]}
              >
                <Input placeholder="Şirket adı" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="contactPerson"
                label="İletişim Kişisi"
              >
                <Input placeholder="İletişim kişisi" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="phone"
                label="Telefon"
                rules={[{ required: true, message: 'Telefon gereklidir' }]}
              >
                <Input placeholder="Telefon" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="email"
                label="Email"
                rules={[{ type: 'email', message: 'Geçerli bir email adresi giriniz' }]}
              >
                <Input placeholder="Email" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="category"
                label="Kategori"
                rules={[{ required: true }]}
              >
                <Select placeholder="Kategori seçiniz">
                  <Option value="fuel">Yakıt</Option>
                  <Option value="lubricant">Yağ</Option>
                  <Option value="equipment">Ekipman</Option>
                  <Option value="service">Hizmet</Option>
                  <Option value="food">Yiyecek</Option>
                  <Option value="other">Diğer</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="paymentTerms"
                label="Ödeme Koşulları"
              >
                <Select placeholder="Ödeme koşulları">
                  <Option value="cash">Nakit</Option>
                  <Option value="net_15">Net 15</Option>
                  <Option value="net_30">Net 30</Option>
                  <Option value="net_60">Net 60</Option>
                  <Option value="other">Diğer</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="notes"
                label="Notlar"
              >
                <TextArea rows={3} placeholder="Notlar" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedSupplier ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => {
                setSupplierModalVisible(false);
                setSelectedSupplier(null);
                form.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Sipariş Modal */}
      <Modal
        title={selectedOrder ? 'Sipariş Düzenle' : 'Yeni Sipariş Oluştur'}
        open={orderModalVisible}
        onCancel={() => {
          setOrderModalVisible(false);
          setSelectedOrder(null);
          orderForm.resetFields();
        }}
        footer={null}
        width={isMobile ? '95%' : 900}
        styles={{ body: { padding: isMobile ? '16px' : '24px', maxHeight: '80vh', overflowY: 'auto' } }}
      >
        <Form
          form={orderForm}
          layout="vertical"
          onFinish={handleSubmitOrder}
        >
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="stationId"
                label="İstasyon"
                rules={[{ required: true, message: 'İstasyon seçiniz' }]}
              >
                <Select placeholder="İstasyon seçiniz">
                  {stations.map((station: any) => (
                    <Option key={station._id} value={station._id}>
                      {station.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="supplierId"
                label="Tedarikçi"
                rules={[{ required: true, message: 'Tedarikçi seçiniz' }]}
              >
                <Select placeholder="Tedarikçi seçiniz">
                  {suppliers.filter(s => s.isActive).map((supplier: any) => (
                    <Option key={supplier._id} value={supplier._id}>
                      {supplier.companyName}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="orderDate"
                label="Sipariş Tarihi"
                rules={[{ required: true }]}
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="expectedDeliveryDate"
                label="Beklenen Teslimat Tarihi"
              >
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="status"
                label="Durum"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="pending">Beklemede</Option>
                  <Option value="confirmed">Onaylandı</Option>
                  <Option value="processing">İşleniyor</Option>
                  <Option value="shipped">Kargoda</Option>
                  <Option value="delivered">Teslim Edildi</Option>
                  <Option value="cancelled">İptal</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="paymentStatus"
                label="Ödeme Durumu"
                rules={[{ required: true }]}
              >
                <Select>
                  <Option value="pending">Beklemede</Option>
                  <Option value="partial">Kısmi</Option>
                  <Option value="paid">Ödendi</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.List name="items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card key={key} style={{ marginBottom: 16 }} size="small">
                    <Row gutter={[16, 16]}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'productName']}
                          label="Ürün Adı"
                          rules={[{ required: true }]}
                        >
                          <Input placeholder="Ürün adı" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          {...restField}
                          name={[name, 'quantity']}
                          label="Miktar"
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'unit']}
                          label="Birim"
                        >
                          <Select>
                            <Option value="adet">Adet</Option>
                            <Option value="litre">Litre</Option>
                            <Option value="kg">Kg</Option>
                            <Option value="paket">Paket</Option>
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'unitPrice']}
                          label="Birim Fiyat"
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={0} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={8}>
                        <Form.Item
                          {...restField}
                          name={[name, 'taxRate']}
                          label="KDV (%)"
                        >
                          <InputNumber min={0} max={100} style={{ width: '100%' }} />
                        </Form.Item>
                      </Col>
                      <Col xs={24}>
                        <Button type="link" danger onClick={() => remove(name)}>
                          Kalemi Sil
                        </Button>
                      </Col>
                    </Row>
                  </Card>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    Kalem Ekle
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="discount"
                label="İndirim"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="shippingCost"
                label="Kargo Ücreti"
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24}>
              <Form.Item
                name="notes"
                label="Notlar"
              >
                <TextArea rows={3} placeholder="Notlar" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {selectedOrder ? 'Güncelle' : 'Oluştur'}
              </Button>
              <Button onClick={() => {
                setOrderModalVisible(false);
                setSelectedOrder(null);
                orderForm.resetFields();
              }}>
                İptal
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Suppliers;

